import {ConsumptionSummaryUnitEnum, DiscountTypeEnum, PromotionTypeEnum, RadioGroupEnum} from "@/enums"

import {IDescription, IRadioGroupOption} from "@/interface";
import {useI18n} from "vue-i18n";
import {AvailablePromotions} from "@/models";

export interface IProductPrivate {
    id: number,
    guid: string,
    name: string,
    type: string,
    status: string,
    descriptions: IDescription,
    priority?: string,
    pricing: IProductPricing,
    for_residential: boolean,
    for_professional: boolean,
    voice: IProductVolume;
    data: IProductVolume;
    sms: IProductVolume;
    currentActiveOption?: number;
}

export interface IOptionPrivate {
    id: number,
    guid: string,
    name: string,
    status: string,
    descriptions: IDescription,
    pricing: IProductPricing,
    for_residential: boolean,
    for_professional: boolean,
    options?: Array<IProductOption>,
    voice?: IProductVolume;
    data?: IProductVolume;
    sms?: IProductVolume;
    currentActiveOption?: number;
}

interface IProductPricing {
    price: number,
    billable: boolean,
    invoice_product?: boolean
}
interface IProductVolume {
    limit_amount: number,
    unit: ConsumptionSummaryUnitEnum,
}

interface IProductIncompatibleOption {
    guid: string,
    id: number,
    name: string
}

export interface IProductOption {
    id?: number,
    name: string,
    descriptions: IDescription,
    price: number,
    for_residential: boolean,
    for_professional: boolean,
    max_combination: number,
    incompatible_options: IProductIncompatibleOption[]
}

export interface IProductPromotion {
    id?: number,
    code: string,
    descriptions: IDescription,
    discount_amount: number,
    discount_type: DiscountTypeEnum,
    from: string,
    to: string,
    type: PromotionTypeEnum
}

export interface IProduct {
    id: number,
    guid: string,
    name: string,
    descriptions: IDescription,
    technical_descriptions: IDescription,
    pricing: IProductPricing,
    for_residential: boolean,
    for_professional: boolean,
    data?: IProductVolume;
    voice?: IProductVolume;
    sms?: IProductVolume;
}

export interface IRadioGroupPromotion {
    price: number,
    promo: boolean,
    promoPrice: number,
    description?: IDescription[],
    id?: number
}

export class Product implements IProduct {
    id: number;
    guid: string;
    name: string;
    descriptions: IDescription;
    technical_descriptions: IDescription;
    pricing: IProductPricing;
    for_residential: boolean;
    for_professional: boolean;
    data?: IProductVolume;
    voice?: IProductVolume;
    sms?: IProductVolume;

    constructor(product: IProduct) {
        this.id = product.id ?? 0;
        this.guid = product.guid ?? '';
        this.name = product.name ?? 'Default product';
        this.descriptions = product.descriptions ?? {fr:'',nl:'',de:'',en:''};
        this.technical_descriptions = product.technical_descriptions ?? {fr:'',nl:'',de:'',en:''};
        this.pricing = product.pricing;
        this.for_professional = product.for_professional ?? false;
        this.for_residential = product.for_residential ?? true;
        this.data = product.data;
        this.voice = product.voice;
        this.sms = product.sms;
    }

    radioGroupOption(promo?: IRadioGroupPromotion): IRadioGroupOption{
        const { t, locale } = useI18n();
        const data = this.data ? `${this.data.limit_amount===0 ? t('common.PRODUCT.DATA_UNLIMITED') : this.data.limit_amount + this.data.unit}` : '';
        const voice = this.voice ? `${this.voice.limit_amount===0 ? t('common.PRODUCT.MIN_UNLIMITED') : this.voice.limit_amount +' '+ t('common.PRODUCT.MINUTES')}` : '';
        const sms = this.sms ? `${this.sms.limit_amount===0 ? t('common.PRODUCT.SMS_UNLIMITED') : this.sms.limit_amount +' '+ this.sms.unit}` : '';

        return {
            guid: this.guid,
            label: this.descriptions[locale.value] || '',
            value: this.id,
            descriptionLight: `${data} + ${voice} + ${sms}`,
            description: this.technical_descriptions[locale.value],
            sideType: RadioGroupEnum.Price,
            side: promo ? promo : {price: this.pricing.price, promo: false, promoPrice: 0},
        };
    }

    calculatePrice(promo: AvailablePromotions): number {
        let total = this.pricing.price;

        if(promo?.promotion?.discount_type === DiscountTypeEnum.percentage) {
            total = (promo.promotion.discount_amount || 1 / 100) * total;
        }
        if(promo?.promotion?.discount_type === DiscountTypeEnum.amount) {
            total = total - Math.abs(promo.promotion.discount_amount || 0);
        }

        return total;
    }
}